import React, { useState, useEffect } from 'react';
import { DatePicker, Checkbox, Button, Spin, Modal, Tooltip } from 'antd';

import 'antd/dist/reset.css';
import "assets/styles/Home/HomeUI/TimeSeries/time-series-menu.css";
import { disabledDate } from 'utils/dateFunctions';
import dayjs from "dayjs";
import { notification } from "antd";
import L from 'leaflet';

import { getLayerImageByName } from 'assets/imgs/imageIndex';

import LayersAndResolutions from 'data/layers/LayersAndResolutions.json';
import homeService from 'services/homeService';
import { useNotificationHandler } from 'hooks/useNotificationHandler';

const { RangePicker } = DatePicker;

const TimeSeriesMenu = (props) => {
    const {
        type, selectedResolutions, setSelectedResolutions,
        startDate, setStartDate, endDate, setEndDate,
        loading, submitQuery, handleFunction, setIsButtonClicked,
        tscCoords, tscModeRef, markerModeRef, setIsMarkerButtonClicked, mapRef, isVisible
    } = props;

    const [currentOption, setCurrentOption] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(isVisible);
    const [isChoosingCoordinates, setIsChoosingCoordinates] = useState(false);
    const [bbox, setBbox] = useState(null);
    const [activeButton, setActiveButton] = useState(null);
    const [dates, setDates] = useState([]);

    const layers = LayersAndResolutions.layers;
    const currentLayer = layers.find((layer) => layer.longName == currentOption) || null; // We save the current layer.
    const {contextHolder, notifyError} = useNotificationHandler();

    // DatePicker change handlers: reset active button
    const handleRangeChange = (dates) => {
        if (dates) {
            const [start, end] = dates;
            setStartDate(start);
            setEndDate(end);
        }
    };

    const handleCheckboxChange = (setSelectedResolutions, key, resolution, resolutionType) => {
        setSelectedResolutions(prevState => {
            const currentSelection = prevState[key] || { space: null, time: null };

            // Obtain available resolutions for the layer.
            const availableSpatialResolutions = currentLayer?.spaceResolution || [];
            const availableTemporalResolutions = currentLayer?.timeResolution || [];

            // Toggle the resolution
            let updatedSelection = {
                ...currentSelection,
                [resolutionType]: currentSelection[resolutionType] === resolution ? null : resolution // If the resolution has already been added, it doesn't get added,
            };

            // If we're checking, select the counterpart resolution if only one option is available.
            if (updatedSelection[resolutionType] !== null) {
                if (resolutionType === 'space' && availableTemporalResolutions.length === 1) {
                    updatedSelection.time = availableTemporalResolutions[0];
                } else if (resolutionType === 'time' && availableSpatialResolutions.length === 1) {
                    updatedSelection.space = availableSpatialResolutions[0];
                }
            }

            // If both resolutions are null, remove the key from the state
            if (updatedSelection.space === null && updatedSelection.time === null) {
                const { [key]: _, ...rest } = prevState; // Remove the key
                return rest;
            }

            // Update only the current option's selected resolutions
            if (type === 'chart') {
                return { 
                    ...prevState,
                    [key]: updatedSelection
                }
            } else {
                return {
                    [key]: updatedSelection
                };
            }

        });
    };

    const handleOptionClick = (layerData) => {
        setCurrentOption(layerData);
        setIsModalVisible(true);
    };

    useEffect(() => {
        const fetchDates = async () => {
            const jsonObject = Object.keys(selectedResolutions).map(folderName => ({
                folder: folderName,
                timeResolution: selectedResolutions[folderName].time,
                spaceResolution: selectedResolutions[folderName].space
            }));

            const hasValidEntry = jsonObject.some(
                entry => entry.timeResolution && entry.spaceResolution
            );

            if (hasValidEntry) {
                const availableDates = await homeService.getCommonDates(jsonObject);
                setDates(availableDates);

                // Reset start and end dates
                setStartDate(null);
                setEndDate(null);

                // If there is a selected button for entries, apply it
                if (activeButton) {
                    const numEntries = parseInt(activeButton.replace('last', ''));
                    if (numEntries && availableDates.length > 1) {
                        const newDates = availableDates.slice(-Math.min(numEntries, availableDates.length));
                        
                        // Convert the date strings to moment/dayjs objects
                        const startDate = dayjs(newDates[0], "YYYY-MM-DD");
                        const endDate = dayjs(newDates[newDates.length - 1], "YYYY-MM-DD");

                        setStartDate(startDate);
                        setEndDate(endDate);
                    } else {
                        notifyError("Error fetching dates", "No coinciding dates between layers", 5);
                    }
                }
            } else {
                console.warn("No valid entries with both time and space resolutions.");
            }
        };

        fetchDates();
    }, [selectedResolutions]);

    const handleChooseCoordinatesClick = () => {
        setIsChoosingCoordinates(true);

        if (mapRef.current) {
            mapRef.current.getContainer().style.cursor = 'crosshair'; // Change the cursor over the map to a crosshair.
            mapRef.current.on('click', handleMapClick); // Enables click events over the map.
        }
    };

    const handleMapClick = (event) => {
        const { lat, lng } = event.latlng;
        tscCoords.lat = lat;
        tscCoords.lng = lng;
        setIsChoosingCoordinates(false); // Deshabilitar la selección de coordenadas una vez seleccionadas
        
        if (mapRef.current) {
            mapRef.current.getContainer().style.cursor = '';
            mapRef.current.off('click', handleMapClick);  // Desactivar el evento después de capturar las coordenadas
        }
    };

    const handleDrawRectangleClick = () => {
        setIsChoosingCoordinates(true); // Indicador de que estamos en modo dibujo

        if (mapRef.current) {
            mapRef.current.dragging.disable();

            // Cambia el cursor del mapa a una cruz para indicar el inicio del dibujo
            mapRef.current.getContainer().style.cursor = 'crosshair';

            let startPoint = null;
            let rectangle = null;

            // Evento al hacer clic para marcar el primer punto
            const onMouseDown = (event) => {
                startPoint = event.latlng;

                // Cambia el cursor a un estilo de "arrastre"
                mapRef.current.getContainer().style.cursor = 'move';

                // Evento al mover el ratón para visualizar el rectángulo
                mapRef.current.on('mousemove', onMouseMove);
            };

            const onMouseMove = (event) => {
                if (startPoint) {
                    const endPoint = event.latlng;

                    // Si ya existe un rectángulo, lo eliminamos para redibujarlo
                    if (rectangle) {
                        rectangle.remove();
                    }

                    // Dibujamos un nuevo rectángulo en base a startPoint y endPoint
                    rectangle = L.rectangle([startPoint, endPoint], {
                        color: '#3388ff',
                        weight: 2,
                    }).addTo(mapRef.current);
                }
            };

            // Evento al soltar el ratón para finalizar el rectángulo
            const onMouseUp = (event) => {
                const endPoint = event.latlng;

                // Comprobamos si el rectángulo se dibujó correctamente
                if (!rectangle) {
                    // Mostrar un mensaje de advertencia si no se dibujó el rectángulo
                    notification.warning({
                        message: "Area not drawn",
                        description: "Click and drag to draw the area of interest.",
                        duration: 5,
                        style: {
                            backgroundColor: '#fff', color: '#000', borderRadius: '5px', position: 'fixed', top: 0, right: 0},
                    });
                    // Restauramos el cursor y limpiamos los eventos
                    mapRef.current.getContainer().style.cursor = '';
                    mapRef.current.off('mousedown', onMouseDown);
                    mapRef.current.off('mousemove', onMouseMove);
                    mapRef.current.off('mouseup', onMouseUp);
                    mapRef.current.dragging.enable();
                    setIsChoosingCoordinates(false);
                    return; // No continuar si no hay rectángulo
                }

                // Extraemos las coordenadas del rectángulo (bbox)
                const bounds = rectangle.getBounds();
                const bbox = {
                    southWest: bounds.getSouthWest(),
                    northEast: bounds.getNorthEast(),
                };

                // console.log('Bounding Box:', bbox);

                // Aquí puedes guardar o procesar la bbox según tus necesidades
                setBbox(bbox);

                // Restauramos el cursor y limpiamos los eventos
                mapRef.current.getContainer().style.cursor = '';
                mapRef.current.off('mousedown', onMouseDown);
                mapRef.current.off('mousemove', onMouseMove);
                mapRef.current.off('mouseup', onMouseUp);
                mapRef.current.dragging.enable();
                rectangle.remove();

                setIsChoosingCoordinates(false); // Finalizamos el modo dibujo
            };

            // Activamos los eventos de dibujo en el mapa
            mapRef.current.on('mousedown', onMouseDown);
            mapRef.current.on('mouseup', onMouseUp);
        }
    };



    const handleModalCancelParent = () => {
        handleFunction(setIsButtonClicked, tscModeRef, markerModeRef, setIsMarkerButtonClicked);
    };

    const handleModalCancel = (currentOption) => {
        if (selectedResolutions[currentOption]) {
            const { [currentOption]: _, ...rest } = selectedResolutions; // Remove the key
            setSelectedResolutions(rest);
        }

        setIsModalVisible(false);
    };

    const handleModalOk = () => {
        if (selectedResolutions[currentOption] && (!selectedResolutions[currentOption].space || !selectedResolutions[currentOption].time)) {
            notification.warning({
                message: 'Incomplete Selection',
                description: 'Please fill both space and time resolutions before proceeding.',
                style: {
                    backgroundColor: '#fff', color: '#000', borderRadius: '5px', position: 'fixed', top: 0, right: 0
                },
            });
        } else {
            setIsModalVisible(false);
        }
    };

    const handleLastEntries = (numEntries) => {
        // Check if there are enough entries in the array
        const availableEntries = dates.length;
        const startIndex = availableEntries >= numEntries ? availableEntries - numEntries : 0;

        const startDateRaw = dates[startIndex];
        const endDateRaw = dates[availableEntries - 1];

        // Convert the date strings to moment/dayjs objects
        const startDate = dayjs(startDateRaw, "YYYY-MM-DD");
        const endDate = dayjs(endDateRaw, "YYYY-MM-DD");
        if(dates.length !== 0){
            setStartDate(startDate);
            setEndDate(endDate);
        }
        
        setActiveButton(`last${numEntries}`);
    };

    const title = {
        anim: 'Time Series Animation',
        render: 'Time Series Render',
        chart: 'Time Series Chart',
    }[type] || 'Invalid Type';

    return (
        <Modal
            title={title}
            open={!isChoosingCoordinates}
            onCancel={handleModalCancelParent}
            onOk={handleModalOk}
            width={700}
            zIndex={1000}
            style={{ marginTop: '50px' }}
            footer={null}
        >
            { contextHolder }
            <div className="option-key-list">
                {
                    layers.map(layerData => (
                        <Tooltip title={layerData.productName} key={layerData.shortName} placement="bottom">

                            <div
                                key={layerData.shortName}
                                className={`option-key-item ${currentOption === layerData.longName ? 'highlight' : ''} ${selectedResolutions[layerData.longName] ? 'selected' : ''}`}
                                onClick={() => handleOptionClick(layerData.longName)}
                            >
                                <img
                                    src={getLayerImageByName(layerData.shortName)}
                                    className="option-icon"
                                />
                                    <span>{layerData.displayName ?? layerData.longName}</span>
                            </div>

                        </Tooltip>
                    ))
                }
            </div>

            <div
                className="tsm-bottom-section"
                style={{
                    display: "flex",
                    justifyContent: "space-around",
                    gap: "20px",
                    marginTop: "30px",
                }}
            >
                {/* Buttons */}
                <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                    {/* First row */}
                    <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                            className={`time-range-button ${activeButton === "last10" ? "active" : ""}`}
                            onClick={() => handleLastEntries(10)}
                        >
                            Last 10 Entries
                        </Button>
                        <Button
                            className={`time-range-button ${activeButton === "last25" ? "active" : ""}`}
                            onClick={() => handleLastEntries(25)}
                        >
                            Last 25 Entries
                        </Button>
                    </div>

                    {/* Second row */}
                    <div style={{ display: "flex", gap: "10px" }}>
                        <Button
                            className={`time-range-button ${activeButton === "last50" ? "active" : ""}`}
                            onClick={() => handleLastEntries(50)}
                        >
                            Last 50 Entries
                        </Button>
                        <Button
                            className={`time-range-button ${activeButton === "last100" ? "active" : ""}`}
                            onClick={() => handleLastEntries(100)}
                        >
                            Last 100 Entries
                        </Button>
                    </div>
                </div>

                {/* Date Pickers */}
                <div className="ts-menu" style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <RangePicker
                        className="datepicker"
                        format="DD/MM/YY"
                        placeholder={['Start Date', 'End Date']}
                        value={ [startDate, endDate] }
                        onChange={ handleRangeChange }
                        style={{ marginTop: "20px" }}
                        disabledDate={(current) => disabledDate(dates, current)}
                    />
                </div>
                
            </div>

            {
                type === 'chart' && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", gap: "10px", marginTop: "20px" }}>
                        <Button onClick={handleChooseCoordinatesClick}>Choose coordinates</Button>
                        {tscCoords.lat && tscCoords.lng && (
                            <div className="coordinates-display">
                                <p>Lat: {Number(tscCoords.lat.toFixed(4))}, Lng: {Number(tscCoords.lng.toFixed(4))}</p>
                            </div>
                        )}
                    </div>
                )
            }

            {
                type !== 'chart' && (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", gap: "10px", marginTop: "20px" }}>
                        <Button onClick={handleDrawRectangleClick}>Choose area</Button>
                        {
                            bbox && (
                                <div className="coordinates-display">
                                    <ul style={{ margin: 0 }}>
                                        <li><strong>South-West:</strong> Lat {bbox.southWest.lat.toFixed(4)}, Lng {bbox.southWest.lng.toFixed(4)}</li>
                                        <li><strong>North-East:</strong> Lat {bbox.northEast.lat.toFixed(4)}, Lng {bbox.northEast.lng.toFixed(4)}</li>
                                    </ul>
                                </div>
                            )
                        }
                    </div>
                )
            }

            <div className="tsm-bottom-buttons">
                <Spin spinning={loading} size="large" />
                <Button
                    onClick={() => submitQuery(startDate, endDate, selectedResolutions, mapRef, bbox)}
                    disabled={
                        !startDate ||
                        !endDate ||
                        !selectedResolutions ||
                        Object.keys(selectedResolutions).length === 0 ||
                        (!bbox && (!tscCoords?.lat || !tscCoords?.lng)) ||
                        (!tscCoords?.lat && !tscCoords?.lng && !bbox)
                    }
                >
                    SUBMIT QUERY
                </Button>
            </div>

            <Modal
                title={currentOption}
                open={isModalVisible}
                onCancel={() => { handleModalCancel(currentOption) }}
                onOk={handleModalOk}
                width={600}
                zIndex={5000}
                closable={false}
            >
                <div className="resolutions">
                    <div className="space-res">
                        {
                            currentLayer?.spaceResolution?.map((resolution, index) => (
                                <Checkbox
                                    key={index}
                                    checked={selectedResolutions[currentOption]?.space === resolution}
                                    onChange={() => handleCheckboxChange(setSelectedResolutions, currentOption, resolution, 'space')}
                                >
                                    { resolution }
                                </Checkbox>
                            ))
                        }
                    </div>
                    <div className="time-res">
                        {
                            currentLayer?.timeResolution?.map((resolution, index) => (
                                <Checkbox
                                    key={index}
                                    checked={selectedResolutions[currentOption]?.time === resolution}
                                    onChange={() => handleCheckboxChange(setSelectedResolutions, currentOption, resolution, 'time')}
                                >
                                    { resolution }
                                </Checkbox>
                            ))
                        }
                    </div>
                </div>
            </Modal>
        </Modal>
    );
};

export default TimeSeriesMenu;
